import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RolePermissionsPanel = ({ permissions, rolePermissions }) => {
  const [selectedRole, setSelectedRole] = useState('admin');

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full system access', icon: 'ShieldCheck', color: 'text-error' },
    { value: 'operator', label: 'Operator', description: 'Manage projects, permits & customers', icon: 'Shield', color: 'text-accent' },
    { value: 'viewer', label: 'Viewer', description: 'View-only access', icon: 'Eye', color: 'text-blue-600' }
  ];

  const groupedPermissions = permissions?.reduce((acc, perm) => {
    const resource = perm?.resource || 'other';
    if (!acc?.[resource]) acc[resource] = [];
    acc?.[resource]?.push(perm);
    return acc;
  }, {});

  const hasPermission = (permissionId) => {
    return rolePermissions?.[selectedRole]?.some(rp => rp?.permissionId === permissionId);
  };

  const formatResource = (resource) =>
    resource?.charAt(0)?.toUpperCase() + resource?.slice(1);

  const getResourceIcon = (resource) => {
    const icons = {
      customers: 'Users',
      projects: 'FolderOpen',
      permits: 'FileText',
      contractors: 'Wrench',
      reports: 'BarChart2',
      team: 'UserCheck',
      settings: 'Settings'
    };
    return icons?.[resource] || 'Folder';
  };

  const grantedCount = permissions?.filter(p => hasPermission(p?.id))?.length || 0;
  const totalCount = permissions?.length || 0;

  return (
    <div className="space-y-6">
      {/* Role Selector */}
      <div className="bg-card rounded-xl p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Role to View Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roles?.map((role) => (
            <button
              key={role?.value}
              onClick={() => setSelectedRole(role?.value)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedRole === role?.value
                  ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={role?.icon}
                  size={20}
                  className={selectedRole === role?.value ? 'text-primary' : role?.color}
                />
                <h4 className="font-semibold text-foreground">{role?.label}</h4>
              </div>
              <p className="text-sm text-muted-foreground">{role?.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Permission Summary */}
      <div className="bg-card rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            {roles?.find(r => r?.value === selectedRole)?.label} Permissions
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {grantedCount} of {totalCount} granted
            </span>
            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-success rounded-full transition-all"
                style={{ width: totalCount > 0 ? `${(grantedCount / totalCount) * 100}%` : '0%' }}
              />
            </div>
          </div>
        </div>

        {!permissions?.length ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No permissions data available.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedPermissions || {})?.map(([resource, perms]) => (
              <div key={resource}>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name={getResourceIcon(resource)} size={16} className="text-primary" />
                  {formatResource(resource)}
                  <span className="text-xs text-muted-foreground font-normal">
                    ({perms?.filter(p => hasPermission(p?.id))?.length}/{perms?.length})
                  </span>
                </h4>
                <div className="space-y-2 ml-6">
                  {perms?.map((perm) => {
                    const granted = hasPermission(perm?.id);
                    return (
                      <div
                        key={perm?.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          granted ? 'border-success/20 bg-success/5' : 'border-border bg-muted/20'
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground capitalize">
                            {perm?.name?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">{perm?.description}</p>
                        </div>
                        <div className={`flex items-center gap-1 ${granted ? 'text-success' : 'text-muted-foreground'}`}>
                          <Icon name={granted ? 'CheckCircle' : 'XCircle'} size={18} />
                          <span className="text-xs font-medium">{granted ? 'Granted' : 'Denied'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePermissionsPanel;